import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useNetworkData } from '../../hooks/network/useNetworkData';
import api from '../../API';
import { AGENT_SETUP_BASE } from '../../constants/setupRoadmapSteps';
import styles from './AgentManagementSetup.module.css';

/**
 * Combined landing for Agent Management: AgentsHome-style content first,
 * then the two-column agents/collections dashboard below (possibly removable later).
 */
function AgentSetupIndex() {
    const { local, isLoading: networkLoading } = useNetworkData();

    const { data: agentsResponse, isLoading: agentsLoading } = useQuery({
        queryKey: ['agents-list'],
        queryFn: async () => {
            const response = await api.get('agent', { query: { activeOnly: false } });
            if (response.status !== 200) throw new Error(`Failed to fetch agents: ${response.status}`);
            return response;
        },
        enabled: !!local,
        staleTime: 5 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

    const { data: collectionsResponse, isLoading: collectionsLoading } = useQuery({
        queryKey: ['collections-list'],
        queryFn: async () => {
            const response = await api.get('agent/rag/collections');
            if (response.status === 503 ||
                (response.data && typeof response.data === 'object' && response.data.code === 'LLM_SERVICES_UNAVAILABLE')) {
                return { status: 503, data: { collections: [] }, servicesUnavailable: true };
            }
            if (response.status !== 200) throw new Error(`Failed to fetch collections: ${response.status}`);
            return response;
        },
        enabled: !!local,
        staleTime: 5 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const agents = agentsResponse?.status === 200 && agentsResponse?.data?.data
        ? agentsResponse.data.data
        : (agentsResponse?.status === 200 && Array.isArray(agentsResponse?.data) ? agentsResponse.data : []);
    const collections = collectionsResponse?.status === 200 && collectionsResponse?.data?.collections
        ? collectionsResponse.data.collections
        : [];
    const servicesUnavailable = collectionsResponse?.servicesUnavailable === true;

    if (networkLoading) {
        return <div>Loading...</div>;
    }

    if (!local) {
        return (
            <div className={styles.container}>
                <h1>Agent Management</h1>
                <p>Please complete the initial server configuration before setting up agents and collections.</p>
                <Link to="/admin/setup/initial" className={styles.backLink}>
                    ← Go to Initial Server Configuration
                </Link>
            </div>
        );
    }

    return (
        <>
            {/* AgentsHome-style content first */}
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                    <h2>What are RAG Agents?</h2>
                    <p>
                        RAG (Retrieval Augmented Generation) Agents are AI assistants that combine the power of Large Language Models (LLMs)
                        with your custom knowledge bases. Each agent can be configured with:
                    </p>
                    <ul>
                        <li><strong>System Prompts:</strong> Customize the agent&apos;s behavior, personality, and response style</li>
                        <li><strong>Knowledge Bases:</strong> Collections of documents that the agent can search through to provide accurate, context-aware answers</li>
                        <li><strong>Model Selection:</strong> Choose which LLM model the agent uses for generating responses</li>
                        <li><strong>Retrieval Settings:</strong> Configure how many documents to retrieve and similarity thresholds</li>
                    </ul>
                    <p>
                        Agents can share knowledge bases (collections) with each other, allowing you to build specialized assistants
                        that all have access to the same underlying data.
                    </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                    <div style={{ padding: '20px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '5px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ marginTop: 0 }}>Manage Agents</h3>
                        <p>Create, edit, and configure RAG agents. Set system prompts, assign knowledge bases, and configure model settings.</p>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                            <Link to={`${AGENT_SETUP_BASE}/manage`} className={styles.createButton} style={{ display: 'inline-block' }}>
                                Manage Agents →
                            </Link>
                            <Link to={`${AGENT_SETUP_BASE}/chat`} style={{ display: 'inline-block', padding: '0.75em 1.5em', border: '1px solid #007bff', borderRadius: '6px', color: '#007bff', textDecoration: 'none', fontWeight: 500 }}>Chat Now</Link>
                        </div>
                    </div>
                    <div style={{ padding: '20px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '5px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ marginTop: 0 }}>Manage Collections</h3>
                        <p>Create and manage knowledge base collections. Collections can be shared across multiple agents.</p>
                        <Link to={`${AGENT_SETUP_BASE}/collections`} className={styles.createButton} style={{ display: 'inline-block', marginTop: '10px' }}>
                            Manage Collections →
                        </Link>
                    </div>
                    <div style={{ padding: '20px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '5px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ marginTop: 0 }}>Manage Documents</h3>
                        <p>Upload, view, and manage documents in your knowledge bases. Documents are organized by collection.</p>
                        <Link to={`${AGENT_SETUP_BASE}/documents`} className={styles.createButton} style={{ display: 'inline-block', marginTop: '10px' }}>
                            Manage Documents →
                        </Link>
                    </div>
                    <div style={{ padding: '20px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '5px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ marginTop: 0 }}>Manage Workflows</h3>
                        <p>Create, edit, and execute workflows that chain multiple agents together. Define step-by-step processes for complex AI tasks.</p>
                        <Link to={`${AGENT_SETUP_BASE}/workflows`} className={styles.createButton} style={{ display: 'inline-block', marginTop: '10px' }}>
                            Manage Workflows →
                        </Link>
                    </div>
                </div>

                <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#e7f3ff', borderRadius: '5px', border: '1px solid #b3d9ff' }}>
                    <h3>Quick Start Guide</h3>
                    <ol>
                        <li><strong>Create Collections:</strong> Start by creating knowledge base collections to store your documents</li>
                        <li><strong>Upload Documents:</strong> Add documents (PDFs or text) to your collections</li>
                        <li><strong>Create Agents:</strong> Create agents and assign them to collections, configure system prompts, and select models</li>
                        <li><strong>Test:</strong> Use the chat interface to test your agents and refine their configuration</li>
                    </ol>
                </div>
            </div>

            {/* Dashboard: two-column agents + collections (existing AgentManagementSetup content) */}
            <h2 style={{ marginTop: '2em', marginBottom: '0.5em' }}>Overview</h2>
            <div className={styles.twoColumnLayout}>
                <div className={styles.column}>
                    <div className={styles.sectionHeader}>
                        <h2>Agents ({agents.length})</h2>
                        <Link to={`${AGENT_SETUP_BASE}/manage`} className={styles.manageLink}>Manage Agents →</Link>
                    </div>
                    {agentsLoading ? (
                        <div className={styles.loading}>Loading agents...</div>
                    ) : agents.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>No agents yet. Create one to get started.</p>
                            <Link to={`${AGENT_SETUP_BASE}/manage`} className={styles.createButton}>Create New Agent</Link>
                        </div>
                    ) : (
                        <div className={styles.cardsList}>
                            {agents.map(agent => (
                                <div key={agent.agentId} className={styles.card}>
                                    <div className={styles.cardHeader}>
                                        <h3 className={styles.cardTitle}>{agent.name}</h3>
                                        <span className={`${styles.statusBadge} ${agent.isActive ? styles.active : styles.inactive}`}>
                                            {agent.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div className={styles.cardContent}>
                                        <div className={styles.cardInfo}>
                                            <div><strong>Collections:</strong> {agent.rag_collections?.length || 0}</div>
                                            <div><strong>Model:</strong> {agent.defaultModel || 'llama3.2'}</div>
                                        </div>
                                        {agent.description && <div className={styles.cardDescription}>{agent.description}</div>}
                                        <div className={styles.cardActions}>
                                            <Link to={`${AGENT_SETUP_BASE}/manage/${agent.agentId}`} className={styles.actionLink}>Edit</Link>
                                            <Link to={`${AGENT_SETUP_BASE}/chat?agent=${agent.agentId}`} className={styles.actionLink}>Chat</Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className={styles.column}>
                    <div className={styles.sectionHeader}>
                        <h2>Collections ({collections.length})</h2>
                        <Link to={`${AGENT_SETUP_BASE}/collections`} className={styles.manageLink}>Manage Collections →</Link>
                    </div>
                    {collectionsLoading ? (
                        <div className={styles.loading}>Loading collections...</div>
                    ) : servicesUnavailable ? (
                        <div className={styles.emptyState}>
                            <div style={{ padding: '1em', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', marginBottom: '1em' }}>
                                <p><strong>LLM Services Not Available</strong></p>
                                <p>This server is not configured with LLM capabilities. Ollama and/or ChromaDB services are not available.</p>
                                <p>You can skip this step and configure collections later when LLM services are available.</p>
                            </div>
                        </div>
                    ) : collections.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>No collections yet. Create one to get started.</p>
                            <Link to={`${AGENT_SETUP_BASE}/collections`} className={styles.createButton}>Create New Collection</Link>
                        </div>
                    ) : (
                        <div className={styles.cardsList}>
                            {collections.map(collection => (
                                <div key={collection.collectionName} className={styles.card}>
                                    <div className={styles.cardHeader}>
                                        <h3 className={styles.cardTitle}>{collection.collectionName}</h3>
                                    </div>
                                    <div className={styles.cardContent}>
                                        <div className={styles.cardInfo}>
                                            <div><strong>Documents:</strong> {collection.documentCount || 0}</div>
                                            <div><strong>Chunks:</strong> {collection.chunkCount || 0}</div>
                                        </div>
                                        {collection.agents && collection.agents.length > 0 && (
                                            <div className={styles.cardAgents}>
                                                <strong>Used by:</strong>
                                                <div className={styles.agentTags}>
                                                    {collection.agents.map(agent => (
                                                        <Link key={agent.agentId} to={`${AGENT_SETUP_BASE}/manage`} className={styles.agentTag}>{agent.name}</Link>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        <div className={styles.cardActions}>
                                            <Link to={`${AGENT_SETUP_BASE}/documents?collection=${encodeURIComponent(collection.collectionName)}`} className={styles.actionLink}>View Documents</Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default AgentSetupIndex;
